import React, { useState, useEffect } from 'react';
import StudentSidebar from '../components/StudentSidebar';
import { getLegacyApiUrl } from '../utils/apiUtils';

const StudentAttendanceAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [error, setError] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    module: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch student's enrolled modules
  const fetchStudentModules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = getLegacyApiUrl();
      const response = await fetch(`${apiUrl}/students/enrolled-modules`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch modules: ${response.status}`);
      }

      const data = await response.json();
      console.log('Student modules:', data);
      setModules(data.modules || []);
    } catch (error) {
      console.error('Error fetching student modules:', error);
      setError('Failed to load your enrolled modules');
    }
  };

  // Fetch attendance data for selected filters
  const fetchAttendanceData = async () => {
    if (!filters.module) {
      setError('Please select a module');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = getLegacyApiUrl();
      const queryParams = new URLSearchParams({
        module_code: filters.module,
        ...(filters.dateFrom && { date_from: filters.dateFrom }),
        ...(filters.dateTo && { date_to: filters.dateTo })
      });

      const response = await fetch(`${apiUrl}/attendance/student-analysis?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch attendance data: ${response.status}`);
      }

      const data = await response.json();
      console.log('Attendance data:', data);
      setAttendanceData(data);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setError(error.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset filters and data
  const resetFilters = () => {
    setFilters({
      module: '',
      dateFrom: '',
      dateTo: ''
    });
    setAttendanceData(null);
    setError('');
  };

  // Load modules when component mounts
  useEffect(() => {
    fetchStudentModules();
  }, []);

  // Get attendance color based on percentage
  const getAttendanceColor = (percentage) => {
    if (percentage >= 80) return '#10B981'; // Green
    if (percentage >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };
  return (
    <div style={{display: 'flex', minHeight: '100vh', background: '#F5F6FA'}}>
      <StudentSidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh', background: '#F5F6FA', position: 'relative'}}>
        {/* Page Title */}
        <div style={{padding: '32px 40px 20px 40px'}}>
          <h1 style={{fontSize: 36, fontFamily: 'Outfit, sans-serif', fontWeight: '700', color: '#131A1A', margin: 0}}>
            My Attendance Analysis
          </h1>
          <p style={{fontSize: 16, color: '#798AA3', margin: '8px 0 0 0', fontFamily: 'Outfit, sans-serif'}}>
            View and analyze your attendance records and patterns
          </p>
        </div>

        <div style={{width: 800, margin: '0 auto', padding: '0 40px'}}>
          {/* Filter Section */}
          <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
            <div style={{background: '#F04915', padding: '16px 24px'}}>
              <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                Filter Options
              </h3>
            </div>
            <div style={{padding: '24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px'}}>
                {/* Module Selection */}
                <div>
                  <label style={{display: 'block', marginBottom: '8px', fontSize: 14, fontWeight: '500', color: '#374151', fontFamily: 'Outfit, sans-serif'}}>
                    Choose Module *
                  </label>
                  <select
                    value={filters.module}
                    onChange={(e) => handleFilterChange('module', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Outfit, sans-serif',
                      backgroundColor: 'white',
                      outline: 'none'
                    }}
                  >
                    <option value="">Select a module</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.module_code}>
                        {module.module_code} - {module.module_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* From Date */}
                <div>
                  <label style={{display: 'block', marginBottom: '8px', fontSize: 14, fontWeight: '500', color: '#374151', fontFamily: 'Outfit, sans-serif'}}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Outfit, sans-serif',
                      backgroundColor: 'white',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* To Date */}
                <div>
                  <label style={{display: 'block', marginBottom: '8px', fontSize: 14, fontWeight: '500', color: '#374151', fontFamily: 'Outfit, sans-serif'}}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Outfit, sans-serif',
                      backgroundColor: 'white',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                <button
                  onClick={resetFilters}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Outfit, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Reset
                </button>
                <button
                  onClick={fetchAttendanceData}
                  disabled={!filters.module || loading}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: filters.module && !loading ? '#F04915' : '#D1D5DB',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Outfit, sans-serif',
                    cursor: filters.module && !loading ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s'
                  }}
                >
                  {loading ? 'Loading...' : 'Analyze Attendance'}
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{marginBottom: '24px', padding: '16px', backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '8px'}}>
              <p style={{color: '#DC2626', margin: 0, fontSize: '14px', fontFamily: 'Outfit, sans-serif'}}>
                {error}
              </p>
            </div>
          )}

          {/* Results Section */}
          {attendanceData && (
            <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
              <div style={{background: '#F04915', padding: '16px 24px'}}>
                <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                  Attendance Analysis Results
                </h3>
              </div>
              <div style={{padding: '24px'}}>
                {/* Attendance Summary Cards */}
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px'}}>
                  {/* Attendance Percentage */}
                  <div style={{
                    backgroundColor: '#F8FAFC',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: getAttendanceColor(attendanceData.attendance_percentage),
                      fontFamily: 'Outfit, sans-serif',
                      marginBottom: '8px'
                    }}>
                      {attendanceData.attendance_percentage?.toFixed(1)}%
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      fontFamily: 'Outfit, sans-serif'
                    }}>
                      Attendance Percentage
                    </div>
                  </div>

                  {/* Sessions Attended */}
                  <div style={{
                    backgroundColor: '#F8FAFC',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#10B981',
                      fontFamily: 'Outfit, sans-serif',
                      marginBottom: '8px'
                    }}>
                      {attendanceData.attendance_count}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      fontFamily: 'Outfit, sans-serif'
                    }}>
                      Sessions Attended
                    </div>
                  </div>

                  {/* Total Sessions */}
                  <div style={{
                    backgroundColor: '#F8FAFC',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#3B82F6',
                      fontFamily: 'Outfit, sans-serif',
                      marginBottom: '8px'
                    }}>
                      {attendanceData.total_sessions}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      fontFamily: 'Outfit, sans-serif'
                    }}>
                      Total Sessions
                    </div>
                  </div>
                </div>

                {/* Module and Date Range Info */}
                <div style={{backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '20px'}}>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px'}}>
                    <div>
                      <div style={{fontSize: '12px', color: '#6B7280', fontFamily: 'Outfit, sans-serif', marginBottom: '4px'}}>
                        MODULE
                      </div>
                      <div style={{fontSize: '14px', color: '#111827', fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                        {attendanceData.module?.name} ({attendanceData.module?.code})
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize: '12px', color: '#6B7280', fontFamily: 'Outfit, sans-serif', marginBottom: '4px'}}>
                        DATE RANGE
                      </div>
                      <div style={{fontSize: '14px', color: '#111827', fontFamily: 'Outfit, sans-serif', fontWeight: '500'}}>
                        {attendanceData.date_range?.from} to {attendanceData.date_range?.to}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attendance Status Breakdown */}
                {attendanceData.attendance_records && attendanceData.attendance_records.length > 0 && (
                  <div>
                    <h4 style={{fontSize: '16px', fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: '#111827', marginBottom: '12px'}}>
                      Attendance Records ({attendanceData.attendance_records.length} sessions)
                    </h4>
                    <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                      {attendanceData.attendance_records.map((record, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white',
                          borderRadius: '4px',
                          marginBottom: '2px'
                        }}>
                          <span style={{fontSize: '14px', color: '#111827', fontFamily: 'Outfit, sans-serif'}}>
                            {new Date(record.date).toLocaleDateString()}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            fontFamily: 'Outfit, sans-serif',
                            color: record.status === 'present' ? '#10B981' : '#EF4444',
                            backgroundColor: record.status === 'present' ? '#D1FAE5' : '#FEE2E2',
                            padding: '4px 8px',
                            borderRadius: '12px'
                          }}>
                            {record.status === 'present' ? 'Present' : 'Absent'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* No Data State */}
          {!attendanceData && !loading && !error && (
            <div style={{marginBottom: '24px', borderRadius: 12, borderLeft: '8px solid #F04915', overflow: 'hidden', background: 'white', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)'}}>
              <div style={{background: '#F04915', padding: '16px 24px'}}>
                <h3 style={{fontSize: 20, fontFamily: 'Outfit, sans-serif', fontWeight: '600', color: 'white', margin: 0}}>
                  Attendance Analysis
                </h3>
              </div>
              <div style={{padding: '24px', textAlign: 'center'}}>
                <div style={{
                  fontSize: 18,
                  color: '#6B7280',
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Select a module and click "Analyze Attendance" to view your attendance data
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#9CA3AF',
                  fontFamily: 'Outfit, sans-serif'
                }}>
                  Choose date range to filter specific periods (optional)
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAttendanceAnalysis;